<template>
  <div class="ic-page">
    <div class="ic-portal-header"><UserHeader /></div>

    <main class="ic-content">
      <div v-if="error" class="ic-alert" role="alert">
        <span>{{ error }}</span>
        <button type="button" aria-label="Dismiss message" @click="error = ''">&times;</button>
      </div>

      <div
        v-if="showStatusStrip"
        class="ic-status"
        :class="{
          'ic-status--active': entitlement.hasSubscription,
          'ic-status--notice': pageStatus === 'cancelled',
          'ic-status--free': !entitlement.hasSubscription && entitlement.freeChecksRemaining > 0,
          'ic-status--used': !entitlement.hasSubscription && entitlement.freeChecksRemaining === 0,
        }"
        role="status"
      >
        <div>
          <span v-if="statusMessage" class="ic-status__eyebrow">Checkout update</span>
          <strong>{{ statusTitle }}</strong>
          <p>{{ statusBody }}</p>
        </div>
        <router-link v-if="entitlement.hasSubscription" to="/ingredient-checker" class="ic-status__action">
          Check a product
        </router-link>
      </div>

      <section class="ic-hero">
        <div class="ic-hero__copy">
          <h1>
            <span>Unlimited</span>
            <span class="ic-hero__title-accent">Ingredient Label</span>
            <span>Checking</span>
          </h1>
          <p class="ic-hero__lede">
            Take a photo of any ingredient label and compare it with your UCARI intolerance test results.
          </p>
          <ul class="ic-hero__list">
            <li><span uk-icon="icon: check"></span>See severe and moderate matches at a glance</li>
            <li><span uk-icon="icon: check"></span>Scan directly from your phone or upload a photo</li>
            <li><span uk-icon="icon: check"></span>Keep every result in your personal scan history</li>
          </ul>

          <div class="ic-plan-card">
            <div v-if="entitlementError" class="ic-plan-loading ic-plan-loading--error">
              <span>{{ entitlementError }}</span>
              <button type="button" class="ic-button ic-button--secondary" @click="fetchEntitlement({ retryIfInactive: pageStatus === 'success' })">
                Check Again
              </button>
            </div>

            <div v-else-if="!entitlementLoaded" class="ic-plan-loading">
              <span uk-spinner="ratio: 0.6"></span>
              Checking your access...
            </div>

            <template v-else-if="entitlement.hasSubscription">
              <div class="ic-plan-card__active">
                <span class="ic-plan-card__check" uk-icon="icon: check; ratio: 1.25"></span>
                <div>
                  <span class="ic-plan-card__label">Your subscription is active</span>
                  <strong>Unlimited ingredient checks</strong>
                </div>
              </div>
              <div class="ic-plan-actions">
                <router-link to="/ingredient-checker" class="ic-button ic-button--primary">Check a Product</router-link>
                <button type="button" class="ic-button ic-button--secondary" :disabled="portalLoading" @click="manageSubscription">
                  {{ portalLoading ? 'Opening billing...' : 'Manage Subscription' }}
                </button>
              </div>
            </template>

            <template v-else-if="entitlement.needsBillingAttention">
              <strong>Your subscription needs billing attention</strong>
              <p>Manage your existing subscription to resolve billing and restore unlimited ingredient checks.</p>
              <button type="button" class="ic-button ic-button--primary ic-button--wide" :disabled="portalLoading" @click="manageSubscription">
                {{ portalLoading ? 'Opening billing...' : 'Manage Subscription' }}
              </button>
            </template>

            <template v-else>
              <div class="ic-plan-card__topline">
                <div>
                  <span class="ic-plan-card__label">Choose your plan</span>
                  <strong>Unlimited ingredient checks</strong>
                </div>
                <span v-if="yearlyDiscountPercent" class="ic-save-pill">Save {{ yearlyDiscountPercent }}%</span>
              </div>

              <div class="ic-plan-selector" aria-label="Subscription billing period">
                <button
                  type="button"
                  :class="{ 'is-active': selectedPlan === 'month' }"
                  :aria-pressed="selectedPlan === 'month'"
                  :disabled="subscribeLoading"
                  @click="selectedPlan = 'month'"
                >
                  <span>Monthly</span>
                  <strong>{{ monthlyPlanPrice }}</strong>
                  <small>per month</small>
                </button>
                <button
                  type="button"
                  :class="{ 'is-active': selectedPlan === 'year' }"
                  :aria-pressed="selectedPlan === 'year'"
                  :disabled="subscribeLoading"
                  @click="selectedPlan = 'year'"
                >
                  <span>Yearly</span>
                  <strong>{{ yearlyPlanPrice }}</strong>
                  <small>per year</small>
                </button>
              </div>

              <p v-if="planHighlight" class="ic-plan-highlight">{{ planHighlight }}</p>
              <button type="button" class="ic-button ic-button--primary ic-button--wide" :disabled="subscribeLoading" @click="subscribe">
                {{ subscribeLoading ? 'Opening checkout...' : subscribeButtonLabel }}
              </button>
              <p class="ic-plan-terms">Secure Stripe checkout. Cancel anytime and keep access through the end of your billing period.</p>
            </template>
          </div>
        </div>

        <div class="ic-hero__visual" aria-label="Example ingredient checker result">
          <div class="ic-phone">
            <div class="ic-phone__bar">
              <span></span>
              <strong>Ingredient Checker</strong>
              <span uk-icon="icon: more-vertical"></span>
            </div>
            <div class="ic-scan-preview">
              <div class="ic-label-sheet">
                <span class="ic-label-sheet__title">INGREDIENTS</span>
                <span v-for="line in 7" :key="line" class="ic-label-sheet__line" :style="{ width: `${94 - (line % 3) * 11}%` }"></span>
              </div>
              <span class="ic-scan-frame"><i></i><i></i><i></i><i></i></span>
              <span class="ic-camera-badge" uk-icon="icon: camera; ratio: 1.15"></span>
            </div>
            <div class="ic-result-card">
              <span class="ic-result-card__kicker">Scan complete</span>
              <strong>2 matches found</strong>
              <div class="ic-result-row ic-result-row--severe">
                <span class="ic-result-dot"></span>
                <span><b>Wheat</b><small>Severe match</small></span>
                <span uk-icon="icon: warning"></span>
              </div>
              <div class="ic-result-row ic-result-row--moderate">
                <span class="ic-result-dot"></span>
                <span><b>Soy</b><small>Moderate match</small></span>
                <span uk-icon="icon: info"></span>
              </div>
            </div>
          </div>
          <span class="ic-float-card ic-float-card--fast"><b>Clear results</b><small>from your label comparison</small></span>
          <span class="ic-float-card ic-float-card--history"><span uk-icon="icon: history"></span><b>Saved</b><small>to your history</small></span>
        </div>
      </section>

      <section class="ic-section ic-section--benefits">
        <div class="ic-section__head">
          <span class="ic-eyebrow">Make every label easier</span>
          <h2>More confidence in every aisle</h2>
          <p>Turn a dense ingredient panel into a clear, personalized answer based on your own UCARI results.</p>
        </div>
        <div class="ic-benefits">
          <article>
            <span class="ic-benefit-icon ic-benefit-icon--blue" uk-icon="icon: camera; ratio: 1.35"></span>
            <h3>Skip the manual search</h3>
            <p>Photograph the package instead of reading and comparing every ingredient yourself.</p>
          </article>
          <article>
            <span class="ic-benefit-icon ic-benefit-icon--amber" uk-icon="icon: warning; ratio: 1.35"></span>
            <h3>See important matches first</h3>
            <p>Moderate and severe matches are surfaced clearly so you know where to focus.</p>
          </article>
          <article>
            <span class="ic-benefit-icon ic-benefit-icon--green" uk-icon="icon: history; ratio: 1.35"></span>
            <h3>Remember what you checked</h3>
            <p>Your scan history makes it easier to compare foods, flavors, treats, and formulas later.</p>
          </article>
        </div>
      </section>

      <section class="ic-section ic-section--steps">
        <div class="ic-section__head">
          <span class="ic-eyebrow">How it works</span>
          <h2>From photo to decision in four simple steps</h2>
        </div>
        <div class="ic-steps">
          <article>
            <span class="ic-step-number">01</span>
            <span class="ic-step-icon" uk-icon="icon: camera; ratio: 1.25"></span>
            <h3>Snap the label</h3>
            <p>Photograph the front and ingredient panel or upload images you already have.</p>
          </article>
          <article>
            <span class="ic-step-number">02</span>
            <span class="ic-step-icon" uk-icon="icon: bolt; ratio: 1.25"></span>
            <h3>Scanner reads it</h3>
            <p>The ingredient list is extracted automatically, without tedious manual entry.</p>
          </article>
          <article>
            <span class="ic-step-number">03</span>
            <span class="ic-step-icon" uk-icon="icon: search; ratio: 1.25"></span>
            <h3>Results are compared</h3>
            <p>UCARI checks the label against the severe and moderate items in your results.</p>
          </article>
          <article>
            <span class="ic-step-number">04</span>
            <span class="ic-step-icon" uk-icon="icon: check; ratio: 1.25"></span>
            <h3>Choose confidently</h3>
            <p>Review the matches, save the scan, and make a more informed product decision.</p>
          </article>
        </div>
      </section>

      <section class="ic-section ic-included">
        <div class="ic-included__summary">
          <span class="ic-eyebrow">What subscribers get</span>
          <h2>Keep checking every product.</h2>
          <p>One subscription keeps the ingredient checker connected to your results whenever you need it.</p>
          <div class="ic-mini-metrics">
            <span><b>Unlimited</b><small>ingredient scans</small></span>
            <span><b>Personal</b><small>results comparison</small></span>
            <span><b>Saved</b><small>scan history</small></span>
          </div>
        </div>
        <ul class="ic-included__list">
          <li><span uk-icon="icon: check"></span><span><b>Unlimited scans</b><small>Check as many foods, treats, and formulas as you need while subscribed.</small></span></li>
          <li><span uk-icon="icon: check"></span><span><b>Personalized intolerance matching</b><small>Review severe and moderate ingredient matches without searching manually.</small></span></li>
          <li><span uk-icon="icon: check"></span><span><b>Account-based history</b><small>Return to your previous scans when comparing products later.</small></span></li>
          <li><span uk-icon="icon: check"></span><span><b>Flexible billing</b><small>Choose monthly or yearly access and manage billing securely through Stripe.</small></span></li>
        </ul>
      </section>

      <section class="ic-section ic-faq">
        <div class="ic-section__head">
          <span class="ic-eyebrow">Good to know</span>
          <h2>Ingredient Checker questions, answered</h2>
          <p>Everything you need to know before choosing unlimited access.</p>
        </div>
        <div class="ic-faq__items">
          <details open>
            <summary>What does the Ingredient Checker compare?</summary>
            <p>It reads the product’s ingredient label and compares the extracted ingredients with the severe and moderate items in your UCARI intolerance results.</p>
          </details>
          <details>
            <summary>How does the free ingredient check work?</summary>
            <p>Your first ingredient check is included at no charge. After that free check is used, an active monthly or yearly subscription is required for additional checks.</p>
          </details>
          <details>
            <summary>What is included with unlimited access?</summary>
            <p>You can check as many product labels as you need while your subscription is active, and your completed checks remain available in your account history.</p>
          </details>
          <details>
            <summary>Can I use a photo already on my phone or computer?</summary>
            <p>Yes. You can take another photo or upload an existing image of the product and its ingredient panel.</p>
          </details>
          <details>
            <summary>Can I cancel my subscription?</summary>
            <p>Yes. You can cancel through the secure billing portal and keep access through the end of your current billing period. Partial billing periods are not refunded.</p>
          </details>
        </div>
      </section>

      <section class="ic-closing">
        <div>
          <span class="ic-eyebrow">Ready when the next label is not</span>
          <h2>{{ entitlement.hasSubscription ? 'Your unlimited checks are ready.' : 'Make your UCARI results easier to use every day.' }}</h2>
          <p>{{ entitlement.hasSubscription ? 'Open the ingredient checker and scan your next product.' : 'Choose a plan and keep checking labels whenever a product catches your eye.' }}</p>
        </div>
        <div class="ic-closing__action">
          <router-link v-if="entitlement.hasSubscription" to="/ingredient-checker" class="ic-button ic-button--light">Check a Product</router-link>
          <button v-else type="button" class="ic-button ic-button--light" :disabled="subscribeLoading || !entitlementLoaded" @click="subscribe">
            {{ subscribeLoading ? 'Opening checkout...' : `Get Unlimited Checks - ${selectedPlanPrice}` }}
          </button>
          <small v-if="!entitlement.hasSubscription">{{ selectedPlanCadence }} · secure Stripe checkout</small>
        </div>
      </section>
    </main>

    <UcariFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UserHeader from './UserHeader.vue';
import UcariFooter from './UcariFooter.vue';
import { logActivity } from '@/utils/activity';

export default {
  name: 'IngredientCheckerSubscription',
  components: {
    UserHeader,
    UcariFooter,
  },
  data() {
    return {
      subscribeLoading: false,
      portalLoading: false,
      selectedPlan: 'month',
      error: '',
      entitlementError: '',
      pageStatus: '',
      entitlementLoaded: false,
      entitlement: {
        completedChecks: 0,
        freeChecksIncluded: 1,
        freeChecksRemaining: 1,
        hasSubscription: false,
        needsBillingAttention: false,
        canScan: true,
      },
    };
  },
  created() {
    document.title = 'Unlimited Ingredient Checker | UCARI';
    this.setStatusFromRoute();
    this.fetchEntitlement({ retryIfInactive: this.pageStatus === 'success' });
  },
  mounted() {
    this.logActivity(48);
  },
  watch: {
    '$route.query.status': 'setStatusFromRoute',
  },
  computed: {
    monthlyPlanPrice() {
      return this.formatDisplayPrice(process.env.VUE_APP_INGREDIENT_CHECKER_MONTHLY_DISPLAY_PRICE, '1.99');
    },
    yearlyPlanPrice() {
      return this.formatDisplayPrice(process.env.VUE_APP_INGREDIENT_CHECKER_YEARLY_DISPLAY_PRICE, '19.99');
    },
    selectedPlanPrice() {
      return this.selectedPlan === 'year' ? this.yearlyPlanPrice : this.monthlyPlanPrice;
    },
    selectedPlanCadence() {
      return this.selectedPlan === 'year' ? 'per year' : 'per month';
    },
    yearlyDiscountPercent() {
      const monthly = this.parsePriceValue(this.monthlyPlanPrice);
      const yearly = this.parsePriceValue(this.yearlyPlanPrice);

      if (!monthly || !yearly) return 0;

      const yearlyMonthlyEquivalent = yearly / 12;
      const discount = 1 - (yearlyMonthlyEquivalent / monthly);

      if (discount <= 0) return 0;
      return Math.round(discount * 100);
    },
    subscribeButtonLabel() {
      return this.selectedPlan === 'year'
        ? `Subscribe Yearly for Unlimited Checks${this.yearlyDiscountPercent ? ` and Save ${this.yearlyDiscountPercent}%` : ''}`
        : 'Subscribe Monthly for Unlimited Checks';
    },
    statusMessage() {
      if (this.pageStatus === 'success' && this.entitlement.hasSubscription) {
        return 'Your Unlimited Ingredient Checker subscription is active. You can now keep scanning additional pet food labels.';
      }
      if (this.pageStatus === 'cancelled') {
        return 'Checkout was cancelled.';
      }
      return '';
    },
    showStatusStrip() {
      return this.entitlementLoaded && !this.entitlementError
        && (!this.entitlement.hasSubscription || this.pageStatus);
    },
    entitlementHeadline() {
      if (this.entitlement.needsBillingAttention) return 'Your subscription needs billing attention';
      if (this.entitlement.hasSubscription) return 'Unlimited scans are active on your account';
      if (this.entitlement.freeChecksRemaining > 0) return 'Your free ingredient check is still available';
      return 'Your free ingredient check has already been used';
    },
    entitlementMessage() {
      if (this.entitlement.needsBillingAttention) {
        return 'You already have an Ingredient Checker subscription. Manage it below to resolve billing and restore unlimited checks.';
      }
      if (this.entitlement.hasSubscription) {
        return 'You can keep scanning labels without any additional per-scan limit while your subscription is active.';
      }
      if (this.entitlement.freeChecksRemaining > 0) {
        return 'Use your first scan for free, then unlock unlimited additional checks with a monthly or yearly plan.';
      }
      return 'Subscribe now to keep checking additional foods, treats, and formulas for intolerance matches.';
    },
    statusTitle() {
      if (this.pageStatus === 'success' && this.entitlement.hasSubscription) return 'Subscription confirmed';
      if (this.pageStatus === 'cancelled') return 'Checkout cancelled';
      return this.entitlementHeadline;
    },
    statusBody() {
      if (this.entitlement.needsBillingAttention) return this.entitlementMessage;
      if (this.pageStatus === 'success' && this.entitlement.hasSubscription) return this.statusMessage;
      if (this.pageStatus === 'cancelled') {
        if (this.entitlement.hasSubscription) {
          return 'Your existing subscription is unchanged. You can still manage it below.';
        }
        if (this.entitlement.freeChecksRemaining > 0) {
          return 'No changes were made. Your free ingredient check is still available, or you can choose a plan below when you are ready.';
        }
        return 'No changes were made. You can choose a monthly or yearly plan below whenever you want to continue.';
      }
      return this.entitlementMessage;
    },
    planHighlight() {
      if (!this.entitlementLoaded) {
        return 'We are checking your account before showing subscription actions.';
      }
      if (this.entitlement.hasSubscription) {
        return '';
      }
      if (this.pageStatus === 'cancelled') {
        return 'No subscription was started. Review the plan options below whenever you are ready.';
      }
      if (this.entitlement.freeChecksRemaining > 0) {
        return 'One ingredient check is free. Additional ingredient checks require an active monthly or yearly subscription.';
      }
      return 'Choose a monthly or yearly plan to keep checking pet food labels.';
    },
  },
  methods: {
    logActivity,
    parsePriceValue(value) {
      const numeric = Number.parseFloat(String(value || '').replace(/[^\d.]/g, ''));
      return Number.isFinite(numeric) ? numeric : 0;
    },
    formatDisplayPrice(value, fallback) {
      const normalized = String(value || fallback || '')
        .trim()
        .replace(/^['"]|['"]$/g, '')
        .replace(/^\$/, '');

      if (!normalized) return '';
      return normalized.startsWith('$') ? normalized : `$${normalized}`;
    },
    getAuthConfig() {
      const accessToken = localStorage.getItem('accessToken');
      const cid = localStorage.getItem('cid');

      return {
        withCredentials: true,
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          ...(cid ? { cid } : {}),
        },
      };
    },
    async fetchEntitlement({ retryIfInactive = false } = {}) {
      const retryDelays = retryIfInactive ? [0, 500, 1000, 2000, 4000] : [0];
      let lastError = null;

      this.entitlementError = '';
      this.entitlementLoaded = false;

      for (const delay of retryDelays) {
        if (delay) await new Promise((resolve) => setTimeout(resolve, delay));

        try {
          const { data } = await axios.get(
            `${process.env.VUE_APP_API_URL}/api/label-reader/access`,
            this.getAuthConfig()
          );
          this.entitlement = {
            completedChecks: Number(data?.completed_checks || 0),
            freeChecksIncluded: Number(data?.free_checks_included || 1),
            freeChecksRemaining: Number(data?.free_checks_remaining || 0),
            hasSubscription: Boolean(data?.has_subscription),
            needsBillingAttention: Boolean(data?.needs_billing_attention),
            canScan: Boolean(data?.can_scan),
          };
          lastError = null;

          if (!retryIfInactive || this.entitlement.hasSubscription) break;
        } catch (e) {
          lastError = e;
          console.error('Failed to load ingredient checker entitlement:', e);
        }
      }

      this.entitlementLoaded = true;

      if (lastError) {
        this.entitlementError = 'We could not confirm your subscription status. Please check again before starting another subscription.';
      } else if (retryIfInactive && !this.entitlement.hasSubscription && !this.entitlement.needsBillingAttention) {
        this.entitlementError = 'Stripe is still confirming your subscription. Please check again before starting another subscription.';
      }
    },
    setStatusFromRoute() {
      const status = this.$route.query.status;
      this.pageStatus = status === 'success' || status === 'cancelled' ? status : '';
    },
    async subscribe() {
      this.error = '';
      this.subscribeLoading = true;
      this.logActivity(49);
      try {
        const { data } = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/stripe/ingredient-checker/checkout`,
          { interval: this.selectedPlan },
          this.getAuthConfig()
        );
        if (data && data.url) {
          window.location = data.url;
        } else {
          this.error = 'No checkout URL returned.';
        }
      } catch (e) {
        if (e.response?.data?.code === 'ingredient_checker_subscription_exists') {
          await this.fetchEntitlement();
        } else {
          this.error = e.response?.data?.error || e.message;
        }
      } finally {
        this.subscribeLoading = false;
      }
    },
    async manageSubscription() {
      this.error = '';
      this.portalLoading = true;
      try {
        const { data } = await axios.post(
          `${process.env.VUE_APP_API_URL}/api/stripe/ingredient-checker/portal`,
          {},
          this.getAuthConfig()
        );
        if (data && data.url) {
          window.location = data.url;
        } else {
          this.error = 'No billing portal URL returned.';
        }
      } catch (e) {
        this.error = e.response?.data?.error || e.message;
      } finally {
        this.portalLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.ic-page {
  --ic-navy: #07468D;
  --ic-ink: #243047;
  --ic-muted: #566176;
  --ic-border: #dce4ec;
  --ic-soft: #eef4fb;
  --ic-yellow: #ffc843;
  --ic-pink: #F05798;
  --ic-sans: 'Nunito', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
  min-height: 100vh;
  background: #fff;
  color: var(--ic-ink);
  font-family: var(--ic-sans);
  font-size: 16px;
  font-weight: 400;
  line-height: 1.55;
}

.ic-page h1,
.ic-page h2,
.ic-page h3,
.ic-page p,
.ic-page li,
.ic-page button,
.ic-page a,
.ic-page span,
.ic-page small,
.ic-page strong,
.ic-page b {
  font-family: var(--ic-sans);
}

.ic-page,
.ic-page * {
  box-sizing: border-box;
}

.ic-portal-header,
.ic-content {
  width: min(100%, 1200px);
  margin: 0 auto;
}

.ic-portal-header {
  padding: 0;
}

.ic-portal-header :deep(.uk-navbar-container) {
  background: #fff !important;
}

.ic-content {
  padding: 0 32px 72px;
}

.ic-alert,
.ic-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 22px;
  margin-top: 18px;
  padding: 16px 19px;
  border-radius: 14px;
}

.ic-alert {
  background: #fff0ee;
  color: #8d2c1f;
}

.ic-alert button {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 24px;
  cursor: pointer;
}

.ic-status {
  background: #eef4ff;
  color: #23467f;
}

.ic-status--active { background: #eaf7ee; color: #245c37; }
.ic-status--notice { background: #fff5e8; color: #7a4a2a; }
.ic-status--used { background: #fff0ee; color: #8d2c1f; }
.ic-status__eyebrow { display: block; margin-bottom: 3px; font-size: 11px; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; }
.ic-status strong { display: block; font-size: 18px; }
.ic-status p { margin: 4px 0 0; color: inherit; }
.ic-status__action { flex: none; padding: 11px 16px; border-radius: 8px; background: var(--ic-navy); color: #fff !important; font-weight: 800; }

.ic-hero {
  display: grid;
  grid-template-columns: minmax(0, 1.02fr) minmax(390px, .98fr);
  gap: 64px;
  align-items: center;
  min-height: 650px;
  padding: 72px 0;
}

.ic-eyebrow {
  display: inline-block;
  color: #07468D;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: .12em;
  text-transform: uppercase;
}

.ic-hero h1 {
  max-width: 620px;
  margin: 0 0 26px;
  color: var(--ic-navy);
  font-size: clamp(34px, 3.4vw, 46px);
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -.01em;
}

.ic-hero__eyebrow {
  color: #07468D;
}

.ic-hero__lede {
  max-width: 590px;
  margin: 0;
  color: var(--ic-muted);
  font-size: 18px;
  line-height: 1.6;
}

.ic-hero__list {
  display: grid;
  gap: 10px;
  margin: 25px 0 31px;
  padding: 0;
  list-style: none;
}

.ic-hero__list li { display: flex; align-items: center; gap: 10px; color: #34435c; }
.ic-hero__list [uk-icon] { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 24px; height: 24px; border-radius: 50%; background: #e9f1ff; color: #07468D; }

.ic-plan-card {
  max-width: 610px;
  padding: 23px;
  border: 1px solid var(--ic-border);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(20, 43, 111, .1);
}

.ic-plan-loading { display: flex; align-items: center; gap: 10px; color: var(--ic-muted); }
.ic-plan-loading--error { align-items: flex-start; flex-direction: column; color: #8d2c1f; }
.ic-plan-loading--error .ic-button { min-height: 44px; font-size: 15px; }
.ic-plan-card__topline,
.ic-plan-card__active { display: flex; align-items: center; gap: 16px; }
.ic-plan-card__topline { justify-content: space-between; }
.ic-plan-card__active { justify-content: flex-start; }
.ic-plan-card__label { display: block; margin-bottom: 4px; color: var(--ic-muted); font-size: 13px; }
.ic-plan-card strong { display: block; color: var(--ic-ink); font-size: 18px; }
.ic-plan-card__check { display: inline-flex; align-items: center; justify-content: center; flex: none; width: 45px; height: 45px; border-radius: 50%; background: #eaf7ee; color: #2b7446; }
.ic-save-pill { flex: none; padding: 7px 11px; border-radius: 999px; background: #fff2c8; color: #795000; font-size: 12px; font-weight: 800; }

.ic-plan-selector { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 17px; }
.ic-plan-selector button { display: grid; grid-template-columns: 1fr auto; gap: 2px 10px; align-items: end; padding: 13px 14px; border: 1px solid var(--ic-border); border-radius: 13px; background: #f9fbfe; color: var(--ic-ink); text-align: left; cursor: pointer; }
.ic-plan-selector button.is-active { border-color: #07468D; background: #eef4ff; box-shadow: 0 0 0 2px rgba(7, 70, 141, .1); }
.ic-plan-selector button span { grid-row: 1 / span 2; align-self: center; font-weight: 800; }
.ic-plan-selector button strong { font-size: 16px; }
.ic-plan-selector button small { color: var(--ic-muted); font-size: 11px; }
.ic-plan-highlight { margin: 14px 0 0; color: var(--ic-muted); font-size: 13px; line-height: 1.45; }
.ic-plan-terms { margin: 10px 0 0; color: #738097; font-size: 11px; line-height: 1.45; text-align: center; }
.ic-plan-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 19px; }

.ic-button { display: inline-flex; align-items: center; justify-content: center; min-width: min(100%, 290px); min-height: 62px; padding: 14px 22px; border: 0; border-radius: 9px; font-family: inherit; font-size: 18px; font-weight: 800; line-height: 1.2; text-align: center; cursor: pointer; }
.ic-button:focus-visible { outline: 3px solid var(--ic-yellow); outline-offset: 3px; }
.ic-button:disabled { cursor: wait; opacity: .65; }
.ic-button--primary { background: var(--ic-pink); color: #fff !important; }
.ic-button--secondary { border: 1px solid var(--ic-border); background: #fff; color: var(--ic-navy); }
.ic-button--wide { width: 100%; margin-top: 15px; }
.ic-button--light { background: #fff; color: var(--ic-navy) !important; }

.ic-hero__visual { position: relative; min-height: 620px; padding: 36px 35px; border-radius: 36px; background: radial-gradient(circle at 22% 15%, #fff 0 8%, transparent 31%), linear-gradient(145deg, #eaf1ff, #c9dafb); overflow: hidden; }
.ic-hero__visual::before,
.ic-hero__visual::after { content: ''; position: absolute; border-radius: 50%; background: rgba(255,255,255,.58); }
.ic-hero__visual::before { width: 210px; height: 210px; right: -65px; top: -55px; }
.ic-hero__visual::after { width: 160px; height: 160px; left: -60px; bottom: -45px; }

.ic-phone { position: relative; z-index: 2; width: min(100%, 360px); margin: 0 auto; padding: 14px; border: 7px solid #15275a; border-radius: 36px; background: #f8fafc; box-shadow: 0 30px 60px rgba(20, 43, 111, .28); transform: rotate(2deg); }
.ic-phone__bar { display: grid; grid-template-columns: 24px 1fr 24px; align-items: center; padding: 5px 3px 13px; color: var(--ic-navy); text-align: center; }
.ic-phone__bar > span:first-child { width: 8px; height: 8px; margin-left: 6px; border-radius: 50%; background: #53b772; }
.ic-phone__bar strong { font-size: 14px; }

.ic-scan-preview { position: relative; display: flex; align-items: center; justify-content: center; min-height: 235px; overflow: hidden; border-radius: 22px; background: linear-gradient(145deg, #e9edf1, #f8f5ee); }
.ic-label-sheet { display: flex; flex-direction: column; gap: 9px; width: 72%; padding: 25px 19px; border-radius: 5px; background: #fffdf5; box-shadow: 0 8px 24px rgba(41, 48, 58, .15); transform: rotate(-5deg); }
.ic-label-sheet__title { color: #1d283a; font-size: 13px; font-weight: 900; letter-spacing: .05em; }
.ic-label-sheet__line { height: 5px; border-radius: 3px; background: #9aa3ae; }
.ic-scan-frame { position: absolute; inset: 25px; }
.ic-scan-frame i { position: absolute; width: 30px; height: 30px; border-color: #07468D; border-style: solid; }
.ic-scan-frame i:nth-child(1) { top: 0; left: 0; border-width: 3px 0 0 3px; }
.ic-scan-frame i:nth-child(2) { top: 0; right: 0; border-width: 3px 3px 0 0; }
.ic-scan-frame i:nth-child(3) { right: 0; bottom: 0; border-width: 0 3px 3px 0; }
.ic-scan-frame i:nth-child(4) { bottom: 0; left: 0; border-width: 0 0 3px 3px; }
.ic-camera-badge { position: absolute; right: 19px; bottom: 17px; display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; border-radius: 50%; background: #07468D; color: #fff; box-shadow: 0 7px 15px rgba(7, 70, 141, .28); }

.ic-result-card { margin-top: 13px; padding: 17px; border-radius: 20px; background: #fff; }
.ic-result-card__kicker { display: block; color: #2e7b4a; font-size: 11px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.ic-result-card > strong { display: block; margin: 4px 0 12px; color: var(--ic-ink); font-size: 19px; }
.ic-result-row { display: grid; grid-template-columns: 12px 1fr 24px; gap: 10px; align-items: center; padding: 11px 12px; border-radius: 12px; }
.ic-result-row + .ic-result-row { margin-top: 8px; }
.ic-result-row--severe { background: #f2f2f2; color: #30343a; }
.ic-result-row--moderate { background: #fff7d8; color: #745b00; }
.ic-result-dot { width: 9px; height: 9px; border-radius: 50%; background: currentColor; }
.ic-result-row b,
.ic-result-row small { display: block; color: inherit; }
.ic-result-row small { margin-top: 1px; font-size: 11px; }

.ic-float-card { position: absolute; z-index: 3; display: grid; padding: 13px 15px; border-radius: 15px; background: #fff; box-shadow: 0 14px 30px rgba(20,43,111,.16); }
.ic-float-card b { color: var(--ic-navy); font-size: 15px; }
.ic-float-card small { color: var(--ic-muted); font-size: 10px; }
.ic-float-card--fast { top: 112px; left: 13px; }
.ic-float-card--history { right: 8px; bottom: 102px; grid-template-columns: 24px 1fr; column-gap: 7px; align-items: center; }
.ic-float-card--history [uk-icon] { grid-row: 1 / span 2; color: #07468D; }

.ic-section { padding: 84px 0; }
.ic-section__head { max-width: 790px; margin: 0 auto 52px; text-align: center; }
.ic-section__head h2,
.ic-included h2,
.ic-closing h2 { margin: 10px 0 13px; color: var(--ic-navy); font-size: clamp(30px, 3vw, 42px); font-weight: 800; line-height: 1.15; letter-spacing: -.018em; }
.ic-section__head p,
.ic-included__summary > p { margin: 0; color: var(--ic-muted); font-size: 17px; line-height: 1.6; }

.ic-section--benefits { border-top: 1px solid #edf1f7; }
.ic-benefits { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
.ic-benefits article { min-height: 270px; padding: 30px; border: 1px solid var(--ic-border); border-radius: 22px; background: #fff; box-shadow: 0 15px 34px rgba(20,43,111,.06); }
.ic-benefit-icon { display: inline-flex; align-items: center; justify-content: center; width: 57px; height: 57px; border-radius: 17px; }
.ic-benefit-icon--blue { background: #eaf1ff; color: #07468D; }
.ic-benefit-icon--amber { background: #fff2c8; color: #8b6500; }
.ic-benefit-icon--green { background: #e8f6ed; color: #2a7848; }
.ic-benefits h3,
.ic-steps h3 { margin: 24px 0 8px; color: var(--ic-ink); font-size: 20px; font-weight: 800; line-height: 1.3; }
.ic-benefits p,
.ic-steps p { margin: 0; color: var(--ic-muted); line-height: 1.65; }

.ic-section--steps { margin-right: -24px; margin-left: -24px; padding-right: 24px; padding-left: 24px; background: var(--ic-soft); }
.ic-steps { position: relative; display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; }
.ic-steps::before { content: ''; position: absolute; top: 31px; right: 8%; left: 8%; height: 2px; background: #cbd9ee; }
.ic-steps article { position: relative; z-index: 1; padding: 0 15px; text-align: center; }
.ic-step-number { display: inline-flex; align-items: center; justify-content: center; width: 62px; height: 62px; border: 6px solid var(--ic-soft); border-radius: 50%; background: #07468D; color: #fff; font-weight: 800; box-shadow: 0 0 0 1px #cbd9ee; }
.ic-step-icon { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; margin: 24px auto 0; border-radius: 14px; background: #fff; color: #07468D; }
.ic-steps h3 { margin-top: 17px; font-size: 18px; }

.ic-included { display: grid; grid-template-columns: .92fr 1.08fr; gap: 62px; align-items: center; }
.ic-mini-metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 31px; }
.ic-mini-metrics span { padding: 17px 12px; border-radius: 15px; background: var(--ic-soft); text-align: center; }
.ic-mini-metrics b,
.ic-mini-metrics small { display: block; }
.ic-mini-metrics b { color: #07468D; font-size: 18px; }
.ic-mini-metrics small { margin-top: 3px; color: var(--ic-muted); font-size: 11px; }
.ic-included__list { display: grid; gap: 12px; margin: 0; padding: 0; list-style: none; }
.ic-included__list li { display: grid; grid-template-columns: 43px 1fr; gap: 15px; align-items: center; padding: 18px 20px; border: 1px solid var(--ic-border); border-radius: 17px; background: #fff; }
.ic-included__list li > [uk-icon] { display: inline-flex; align-items: center; justify-content: center; width: 43px; height: 43px; border-radius: 50%; background: #eaf1ff; color: #07468D; }
.ic-included__list b,
.ic-included__list small { display: block; }
.ic-included__list b { color: var(--ic-ink); font-size: 16px; }
.ic-included__list small { margin-top: 4px; color: var(--ic-muted); font-size: 13px; line-height: 1.45; }

.ic-faq { border-top: 1px solid #edf1f7; }
.ic-faq__items { max-width: 850px; margin: 0 auto; }
.ic-faq__items details { border-top: 1px solid var(--ic-border); }
.ic-faq__items details:last-child { border-bottom: 1px solid var(--ic-border); }
.ic-faq__items summary { position: relative; padding: 23px 46px 23px 0; color: var(--ic-ink); font-size: 18px; font-weight: 800; list-style: none; cursor: pointer; }
.ic-faq__items summary::-webkit-details-marker { display: none; }
.ic-faq__items summary::after { content: '+'; position: absolute; top: 18px; right: 5px; color: #07468D; font-size: 27px; font-weight: 400; }
.ic-faq__items details[open] summary::after { content: '−'; }
.ic-faq__items details p { max-width: 760px; margin: 0; padding: 0 46px 24px 0; color: var(--ic-muted); font-size: 15px; line-height: 1.65; }

.ic-closing { display: grid; grid-template-columns: 1fr auto; gap: 50px; align-items: center; padding: 48px 52px; border-radius: 28px; background: linear-gradient(135deg, #07468D, #F05798); color: #fff; overflow: hidden; }
.ic-closing .ic-eyebrow { color: #a9c7ff; }
.ic-closing h2 { max-width: 700px; color: #fff; font-size: clamp(29px, 4vw, 42px); }
.ic-closing p { max-width: 650px; margin: 0; color: #dce8ff; font-size: 16px; line-height: 1.55; }
.ic-closing__action { display: grid; gap: 9px; justify-items: center; }
.ic-closing__action small { color: #dce8ff; font-size: 11px; }

@media (max-width: 980px) {
  .ic-hero { grid-template-columns: 1fr; gap: 44px; }
  .ic-hero__visual { grid-row: 1; min-height: 570px; }
  .ic-benefits { grid-template-columns: 1fr; }
  .ic-benefits article { min-height: 0; }
  .ic-steps { grid-template-columns: 1fr 1fr; gap: 38px 20px; }
  .ic-steps::before { display: none; }
  .ic-included { grid-template-columns: 1fr; gap: 38px; }
  .ic-closing { grid-template-columns: 1fr; gap: 30px; }
  .ic-closing__action { justify-items: start; }
}

@media (max-width: 640px) {
  .ic-content { padding: 0 22px 48px; }
  .ic-status { align-items: flex-start; flex-direction: column; }
  .ic-status__action { width: 100%; text-align: center; }
  .ic-hero { min-height: 0; padding: 46px 0 52px; }
  .ic-hero__copy { grid-row: 1; }
  .ic-hero__visual { grid-row: 2; }
  .ic-hero__lede { font-size: 18px; }
  .ic-plan-card { padding: 18px; }
  .ic-plan-card__topline { align-items: flex-start; }
  .ic-plan-selector { grid-template-columns: 1fr; }
  .ic-plan-actions { grid-template-columns: 1fr; }
  .ic-hero__visual { min-height: 505px; margin: 0 -4px; padding: 28px 18px; border-radius: 26px; }
  .ic-phone { width: min(100%, 315px); padding: 11px; border-width: 5px; border-radius: 30px; }
  .ic-scan-preview { min-height: 190px; }
  .ic-result-card { padding: 13px; }
  .ic-float-card--fast { top: 91px; left: 5px; }
  .ic-float-card--history { right: 3px; bottom: 65px; }
  .ic-section { padding: 64px 0; }
  .ic-section__head { margin-bottom: 38px; }
  .ic-section--steps { margin-right: -22px; margin-left: -22px; padding-right: 22px; padding-left: 22px; }
  .ic-steps { grid-template-columns: 1fr; }
  .ic-steps article { display: grid; grid-template-columns: 54px 1fr; column-gap: 15px; padding: 0; text-align: left; }
  .ic-step-number { grid-row: 1 / span 3; width: 54px; height: 54px; border-width: 5px; }
  .ic-step-icon { display: none; }
  .ic-steps h3 { margin: 4px 0 5px; }
  .ic-mini-metrics { grid-template-columns: 1fr; }
  .ic-included__list li { padding: 16px; }
  .ic-faq__items summary { padding-right: 38px; font-size: 17px; }
  .ic-faq__items details p { padding-right: 30px; }
  .ic-closing { padding: 34px 24px; border-radius: 22px; }
  .ic-closing__action,
  .ic-closing__action .ic-button { width: 100%; }
}

.ic-hero h1 span {
  display: block;
}

.ic-hero h1 .ic-hero__title-accent {
  color: var(--ic-pink);
}

@media (max-width: 480px) {
  .ic-hero h1 {
    margin-bottom: 20px;
    font-size: 32px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ic-page * { scroll-behavior: auto !important; }
}
</style>
